import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { LoginModal, handleGoogleLogin } from '@/components/auth/LoginModal';
import { SectorSelect } from '@/components/ui/SectorSelect';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { 
  Lightbulb, Coins, Waves,
  User, Briefcase, Award, ArrowRight, Upload, FileText, X, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RetraitesPage = () => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [sectorCategories, setSectorCategories] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    lastPosition: '',
    company: '',
    yearsExperience: '',
    expertise: '',
    motivation: '',
    hourlyRate: '',
    sector: ''
  });

  // Fetch sectors on component mount
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sectors/by-category`);
        const data = await response.json();
        setSectorCategories(data.sectors);
      } catch (error) {
        console.error('Failed to fetch sectors:', error);
      }
    };
    fetchSectors();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5 Mo
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format non supporté. Utilisez PDF, DOC ou DOCX.');
        return;
      }
      
      if (file.size > maxSize) {
        toast.error('Le fichier est trop volumineux. Maximum 5 Mo.');
        return;
      }
      
      setCvFile(file);
    }
  };

  const removeCvFile = () => {
    setCvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const benefits = [
    {
      icon: Lightbulb,
      title: t('benefits.retiree.active.title'),
      description: t('benefits.retiree.active.description')
    },
    {
      icon: Coins,
      title: t('benefits.retiree.income.title'),
      description: t('benefits.retiree.income.description')
    },
    {
      icon: Waves,
      title: t('benefits.retiree.impact.title'),
      description: t('benefits.retiree.impact.description')
    }
  ];

  const steps = [
    {
      number: "01",
      title: t('retireesPage.step1.title'),
      description: t('retireesPage.step1.description')
    },
    {
      number: "02",
      title: t('retireesPage.step2.title'),
      description: t('retireesPage.step2.description')
    },
    {
      number: "03",
      title: t('retireesPage.step3.title'),
      description: t('retireesPage.step3.description')
    }
  ];

  const testimonials = [
    {
      name: "Bernard Lefèvre",
      role: "Ex-DG Industrie",
      experience: "38 ans",
      quote: "Après ma carrière, je pensais que mon expertise ne servirait plus. Grâce à sfretire, j'accompagne maintenant des PME en croissance. C'est extrêmement gratifiant.",
      image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=200&q=80"
    },
    {
      name: "Françoise Mercier",
      role: "Ex-DRH Groupe CAC 40",
      experience: "32 ans",
      quote: "Le mentorat me permet de transmettre ce que j'ai appris tout au long de ma carrière. Les échanges avec les jeunes dirigeants sont enrichissants des deux côtés.",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&q=80"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('name', `${formData.firstName} ${formData.lastName}`);
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('phone', formData.phone);
      submitData.append('skills', formData.expertise);
      submitData.append('experience_years', formData.yearsExperience);
      submitData.append('last_position', formData.lastPosition);
      submitData.append('last_company', formData.company);
      submitData.append('bio', formData.motivation);
      submitData.append('hourly_rate', formData.hourlyRate);
      submitData.append('sector', formData.sector);
      
      if (cvFile) {
        submitData.append('cv', cvFile);
      }
      
      const response = await fetch(`${API_URL}/api/retraites/register`, {
        method: 'POST',
        body: submitData
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Save token and user data to auto-login
        if (result.token) {
          localStorage.setItem('sfretire_token', result.token);
          localStorage.setItem('sfretire_user', JSON.stringify(result.user));
        }
        toast.success(t('retireesPage.applicationSent'));
        setShowForm(false);
        setCvFile(null);
        setFormData({
          firstName: '', lastName: '', email: '', password: '', phone: '',
          lastPosition: '', company: '', yearsExperience: '', expertise: '', 
          motivation: '', hourlyRate: '', sector: ''
        });
        // Redirect to dashboard
        window.location.href = '/dashboard/retraite';
      } else {
        toast.error(result.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full">
                  {t('retireesPage.badge')}
                </span>
                <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground">
                  {t('retireesPage.title')}
                </h1>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {t('retireesPage.subtitle')}
                </p>
                <div className="flex gap-4">
                  <Button variant="gold" size="lg" onClick={() => setShowForm(true)}>
                    {t('retireesPage.joinCommunity')}
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80"
                  alt="Senior professional"
                  className="rounded-2xl shadow-elevated w-full h-[400px] object-cover"
                />
                {/* Floating Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="absolute -bottom-6 left-6 right-6 bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-medium"
                >
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-primary" />
                      <span className="text-muted-foreground">500+ {t('retireesPage.experts')}</span>
                    </div>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-2">
                      <Award size={16} className="text-primary" />
                      <span className="text-muted-foreground">30+ {t('retireesPage.avgExp')}</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                {t('retireesPage.whyJoin')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="card-elevated p-6 text-center"
                  >
                    <div className="icon-circle mx-auto mb-4">
                      <IconComponent size={24} />
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 lg:py-20 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                {t('retireesPage.howItWorks')}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('retireesPage.howItWorksSubtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative"
                >
                  <div className="bg-card rounded-xl p-6 border border-border/50 h-full">
                    <span className="text-5xl font-serif font-bold text-primary/20">
                      {step.number}
                    </span>
                    <h3 className="text-lg font-serif font-semibold text-foreground mt-2 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight size={20} className="text-primary/30" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                {t('retireesPage.joinedMovement')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card rounded-xl p-6 border border-border/50"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-serif font-semibold text-foreground">{testimonial.name}</h4>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                      <p className="text-xs text-primary">{testimonial.experience}</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground italic">&quot;{testimonial.quote}&quot;</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 section-dark">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">
                {t('retireesPage.readyToShare')}
              </h2>
              <p className="text-accent-foreground/70 mb-8">
                {t('retireesPage.readySubtitle')}
              </p>
              <Button variant="gold" size="lg" onClick={() => setShowForm(true)}>
                {t('retireesPage.joinCommunity')}
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Registration Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-elevated my-4 sm:my-8 max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground mb-2">
                {t('retireesPage.formTitle')}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {t('retireesPage.formSubtitle')}
              </p>

              {/* Google Sign Up Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4 flex items-center justify-center gap-3 h-12"
                onClick={() => handleGoogleLogin('retraite')}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.firstName')} *</label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.lastName')} *</label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.email')} *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.password')} *</label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                        minLength={6}
                        placeholder="Min. 6 caractères"
                        className="pr-10"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.phone')}</label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(val) => setFormData({...formData, phone: val || ''})}
                      className="mt-1"
                      data-testid="register-phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.sector')} *</label>
                    <div className="mt-1">
                      <SectorSelect
                        value={formData.sector}
                        onChange={(val) => setFormData({...formData, sector: val})}
                        sectors={sectorCategories}
                        placeholder={t('retireesPage.selectSector')}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.lastPosition')} *</label>
                    <Input
                      value={formData.lastPosition}
                      onChange={(e) => setFormData({...formData, lastPosition: e.target.value})}
                      placeholder={t('retireesPage.positionPlaceholder')}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.company')}</label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.yearsExperience')} *</label>
                    <Input
                      value={formData.yearsExperience}
                      onChange={(e) => setFormData({...formData, yearsExperience: e.target.value})}
                      placeholder="Ex: 30"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('retireesPage.expertiseAreas')} *</label>
                    <Input
                      value={formData.expertise}
                      onChange={(e) => setFormData({...formData, expertise: e.target.value})}
                      placeholder={t('retireesPage.expertisePlaceholder')}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">{t('retireesPage.motivation')}</label>
                  <Textarea
                    value={formData.motivation}
                    onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                {/* Tarif horaire */}
                <div>
                  <label className="text-sm font-medium text-foreground">{t('retireesPage.hourlyRate')}</label>
                  <Input
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})}
                    placeholder={t('retireesPage.hourlyRatePlaceholder')}
                    min="0"
                    className="mt-1"
                    data-testid="hourly-rate-input"
                  />
                </div>

                {/* Upload CV */}
                <div>
                  <label className="text-sm font-medium text-foreground">{t('retireesPage.uploadCV')}</label>
                  <div className="mt-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      data-testid="cv-file-input"
                    />
                    
                    {!cvFile ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                        data-testid="cv-upload-zone"
                      >
                        <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm font-medium text-foreground">{t('retireesPage.uploadCV')}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t('retireesPage.cvFormats')}</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20" data-testid="cv-file-preview">
                        <div className="flex items-center gap-3">
                          <FileText className="h-6 w-6 text-primary" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{cvFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('retireesPage.cvSelected')} • {(cvFile.size / 1024).toFixed(0)} Ko
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCvFile}
                          className="p-1 hover:bg-destructive/10 rounded-full transition-colors"
                          data-testid="cv-remove-btn"
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="flex-1">
                    {t('retireesPage.cancel')}
                  </Button>
                  <Button type="submit" variant="gold" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? t('retireesPage.submitting') : t('retireesPage.submit')}
                  </Button>
                </div>

                <p className="text-center text-sm text-muted-foreground pt-2">
                  {t('retireesPage.alreadyRegistered')}{' '}
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setShowLoginModal(true); }}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('retireesPage.loginLink')}
                  </button>
                </p>
              </form>
            </motion.div>
          </div>
        )}

        {/* Login Modal */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          userType="retraite"
          onSwitchToRegister={() => {
            setShowLoginModal(false);
            setShowForm(true);
          }}
        />
      </main>

      <Footer />
    </div>
  );
};

export default RetraitesPage;
